import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, Play, Users, Ticket, Sparkles, Zap, Star, ArrowRight, ChevronRight, Settings, Menu, X, User, LogOut, Home, Film, Video } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import usePageTracking from '../hooks/usePageTracking';
import BannerAd from '../components/BannerAd';
import PageShareButton from '../components/PageShareButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple in-memory cache for events to speed up navigation
let eventsCache = null;
let eventsCacheTime = 0;
const CACHE_DURATION = 30000; // 30 seconds

const LiveEventsLanding = () => {
  usePageTracking('live-events');
  const navigate = useNavigate();
  const [events, setEvents] = useState(eventsCache || []);
  const [loading, setLoading] = useState(!eventsCache);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userTickets, setUserTickets] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');

  useEffect(() => {
    // Only fetch if cache is expired
    const now = Date.now();
    if (!eventsCache || (now - eventsCacheTime) > CACHE_DURATION) {
      fetchEvents();
    }
    
    const token = localStorage.getItem('livestreamToken');
    const customerData = localStorage.getItem('livestreamCustomer');
    setIsLoggedIn(!!token);
    
    if (customerData) {
      try {
        const customer = JSON.parse(customerData);
        setIsAdmin(customer.is_admin === true || customer.is_staff === true);
        setCustomerName(customer.full_name || customer.email || 'User');
        
        // Verify admin status from backend if we have a token
        if (token) {
          verifyAdminStatus(token, customer);
        }
      } catch (e) {
        console.error('Error parsing customer data:', e);
      }
    }
    
    // Also check if admin is logged in separately
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      setIsAdmin(true);
    }
    
    // Fetch user's tickets if logged in
    if (token) {
      fetchUserTickets(token);
    }
  }, []);

  const verifyAdminStatus = async (token, customer) => {
    try {
      const response = await axios.get(`${API}/customer/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const backendCustomer = response.data;
      if (backendCustomer?.is_admin === true) {
        setIsAdmin(true);
        // Update localStorage with correct admin status
        const updatedCustomer = { ...customer, is_admin: true };
        localStorage.setItem('livestreamCustomer', JSON.stringify(updatedCustomer));
      }
    } catch (err) {
      console.error('Error verifying admin status:', err);
    }
  };

  const fetchUserTickets = async (token) => {
    try {
      const response = await axios.get(`${API}/livestream/my-tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Extract event IDs from tickets
      const ticketEventIds = (response.data.tickets || []).map(t => t.event_id);
      setUserTickets(ticketEventIds);
    } catch (err) {
      console.error('Error fetching user tickets:', err);
    }
  };

  const hasTicketForEvent = (eventId) => {
    return userTickets.includes(eventId);
  };

  const handleEventClick = (event) => {
    if (isLoggedIn && hasTicketForEvent(event.id)) {
      // User has a ticket - go directly to watch page
      navigate(`/live/watch/${event.slug}`);
    } else {
      // No ticket - go to event detail page to purchase
      navigate(`/live/event/${event.slug}`);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/livestream/events`);
      const fetchedEvents = response.data.events || [];
      setEvents(fetchedEvents);
      // Update cache
      eventsCache = fetchedEvents;
      eventsCacheTime = Date.now();
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const liveEvents = events.filter(e => e.status === 'live');
  
  // Date-aware categorization: events with dates in the past are "past" regardless of status field
  const now = new Date();
  const upcomingEvents = events.filter(e => {
    if (e.status === 'live') return false;
    if (e.status === 'completed' || e.status === 'ended' || e.status === 'past') return false;
    if (e.event_date) {
      const eventDate = new Date(e.event_date);
      // Add 24 hours buffer - event is "past" the day AFTER the event date
      const endOfEventDay = new Date(eventDate);
      endOfEventDay.setHours(23, 59, 59, 999);
      return endOfEventDay >= now;
    }
    return e.status === 'upcoming';
  });
  
  const pastEvents = events.filter(e => {
    if (e.status === 'live') return false;
    if (e.status === 'completed' || e.status === 'ended' || e.status === 'past') return true;
    if (e.event_date) {
      const eventDate = new Date(e.event_date);
      const endOfEventDay = new Date(eventDate);
      endOfEventDay.setHours(23, 59, 59, 999);
      return endOfEventDay < now;
    }
    return false;
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-hidden">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>LTV Live | Stream Exclusive Concerts, Battles & Shows in 4K/HD</title>
        <meta name="description" content="Experience live events like never before. Watch exclusive concerts, sound clashes, battles & shows in stunning 4K/HD quality. Stream from anywhere with live chat, voting & interactive features." />
        <meta name="keywords" content="live streaming, 4K live stream, HD streaming, live concerts, sound clash, live battles, live shows, stream events online, buy streaming tickets, live voting, live chat, watch live events, LTV Live" />
        <link rel="canonical" href="https://ltvproduction.com/live" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ltvproduction.com/live" />
        <meta property="og:title" content="LTV Live | Experience Events Like Never Before" />
        <meta property="og:description" content="Watch exclusive concerts, battles, shows & more in stunning 4K/HD quality. Purchase your ticket and stream from anywhere in the world. Live chat, voting & interactive features included." />
        <meta property="og:image" content="https://ltvproduction.com/ltv-social-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="LTV Live" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://ltvproduction.com/live" />
        <meta name="twitter:title" content="LTV Live | Experience Events Like Never Before" />
        <meta name="twitter:description" content="Watch exclusive concerts, battles, shows & more in stunning 4K/HD quality. Stream from anywhere in the world." />
        <meta name="twitter:image" content="https://ltvproduction.com/ltv-social-preview.jpg" />
      </Helmet>

      {/* Background - Simplified for better performance */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-purple-600/15 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-red-600/15 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-50 sticky top-0 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div 
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => navigate('/')}
          >
            <img 
              src="/ltv-live-logo.png" 
              alt="LTV Live" 
              className="w-10 h-10 object-contain brightness-0 invert"
            />
            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              LTV Live
            </span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <PageShareButton page="live" buttonStyle="outline" />
            {isLoggedIn ? (
              <>
                {isAdmin && (
                  <Button 
                    size="sm"
                    className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white border-0 rounded-xl"
                    onClick={() => navigate('/admin/livestream')}
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Admin Dashboard
                  </Button>
                )}
                <Button 
                  size="sm"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white border-0 rounded-xl"
                  onClick={() => navigate('/vod')}
                >
                  <Film className="h-4 w-4 mr-1" />
                  VOD
                </Button>
                <Button 
                  size="sm"
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold border-0 rounded-xl"
                  onClick={() => navigate('/creator')}
                >
                  <Video className="h-4 w-4 mr-1" />
                  Creator Package
                </Button>
                <Button 
                  size="sm"
                  className="bg-red-600/80 hover:bg-red-500 text-white border-0 rounded-xl"
                  onClick={() => navigate('/live/account')}
                >
                  <User className="h-4 w-4 mr-1" />
                  My Account
                </Button>
                <Button 
                  size="sm"
                  className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white border-0 rounded-xl"
                  onClick={() => navigate('/live/my-tickets')}
                >
                  <Ticket className="h-4 w-4 mr-1" />
                  My Tickets
                </Button>
                <Button 
                  size="sm"
                  className="bg-white/10 hover:bg-white/20 text-white border-0 rounded-xl"
                  onClick={() => {
                    localStorage.removeItem('livestreamToken');
                    localStorage.removeItem('livestreamCustomer');
                    setIsLoggedIn(false);
                    setIsAdmin(false);
                  }}
                >
                  <LogOut className="h-4 w-4 mr-1" />
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button 
                  size="sm"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white border-0 rounded-xl"
                  onClick={() => navigate('/vod')}
                >
                  <Film className="h-4 w-4 mr-1" />
                  VOD
                </Button>
                <Button 
                  size="sm"
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold border-0 rounded-xl"
                  onClick={() => navigate('/creator')}
                >
                  <Video className="h-4 w-4 mr-1" />
                  Creator Package
                </Button>
                <Button 
                  size="sm"
                  className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white border-0 rounded-xl"
                  onClick={() => navigate('/live/login')}
                >
                  Sign In
                </Button>
                <Button 
                  size="sm"
                  className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white border-0 rounded-xl"
                  onClick={() => navigate('/live/login')}
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10">
            <div className="px-4 py-4 space-y-2">
              {isLoggedIn ? (
                <>
                  {/* User Info */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-xl mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{customerName}</p>
                      <p className="text-xs text-gray-400">Signed in</p>
                    </div>
                  </div>
                  
                  {isAdmin && (
                    <button
                      onClick={() => { navigate('/admin/livestream'); setMobileMenuOpen(false); }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-yellow-400 hover:bg-yellow-500/10 rounded-xl transition-colors"
                    >
                      <Settings className="h-5 w-5" />
                      <span>Admin Dashboard</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => { navigate('/live/my-tickets'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-white/80 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <Ticket className="h-5 w-5" />
                    <span>My Tickets</span>
                  </button>
                  
                  <button
                    onClick={() => { navigate('/live/account'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-white/80 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <User className="h-5 w-5" />
                    <span>My Account</span>
                  </button>
                  
                  <button
                    onClick={() => { navigate('/creator'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-amber-400 hover:bg-amber-500/10 rounded-xl transition-colors"
                  >
                    <Video className="h-5 w-5" />
                    <span>Creator Package</span>
                  </button>
                  
                  <button
                    onClick={() => { navigate('/vod'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-white/80 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <Film className="h-5 w-5" />
                    <span>LTV VOD</span>
                  </button>
                  
                  <button
                    onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-white/80 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <Home className="h-5 w-5" />
                    <span>LTV Production</span>
                  </button>
                  
                  <div className="border-t border-white/10 my-2" />
                  
                  <button
                    onClick={() => {
                      localStorage.removeItem('livestreamToken');
                      localStorage.removeItem('livestreamCustomer');
                      setIsLoggedIn(false);
                      setIsAdmin(false);
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
                  >
                    <LogOut className="h-5 w-5" />
                    <span>Sign Out</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { navigate('/live/login'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-red-600 to-red-500 text-white font-medium rounded-xl"
                  >
                    Sign In
                  </button>
                  
                  <button
                    onClick={() => { navigate('/live/login'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-white/20 text-white/80 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    Create Account
                  </button>
                  
                  <div className="border-t border-white/10 my-2" />
                  
                  <button
                    onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-white/80 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <Home className="h-5 w-5" />
                    <span>LTV Production</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative z-10 pt-12 sm:pt-16 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-red-500/20 to-purple-500/20 border border-red-500/30 mb-6 sm:mb-8">
            <Sparkles className="h-4 w-4 text-red-400" />
            <span className="text-sm font-medium text-red-300">Premium Live Streaming</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-4 sm:mb-6 leading-tight">
            <span className="bg-gradient-to-r from-white via-white to-gray-400 bg-clip-text text-transparent">
              Experience Events
            </span>
            <br />
            <span className="bg-gradient-to-r from-red-400 via-red-500 to-purple-500 bg-clip-text text-transparent">
              Like Never Before
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Watch exclusive concerts, battles, shows & more in stunning 4K / HD quality. 
            Purchase your ticket and stream from anywhere in the world.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button 
              size="lg"
              className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-lg px-8 py-6 rounded-xl shadow-lg shadow-red-500/25"
              onClick={() => document.getElementById('events')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <Play className="h-5 w-5 mr-2" />
              Browse Events
            </Button>
            {isLoggedIn ? (
              <Button 
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl"
                onClick={() => navigate('/live/my-tickets')}
              >
                <Ticket className="h-5 w-5 mr-2" />
                My Tickets
              </Button>
            ) : (
              <Button 
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl"
                onClick={() => navigate('/live/login')}
              >
                Create Account
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-lg mx-auto">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-red-400 to-red-500 bg-clip-text text-transparent">4K / HD</div>
              <div className="text-sm text-gray-500">Quality</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 to-purple-500 bg-clip-text text-transparent">Live</div>
              <div className="text-sm text-gray-500">Streaming</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-400 to-blue-500 bg-clip-text text-transparent">Chat</div>
              <div className="text-sm text-gray-500">Interactive</div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Now Section */}
      {liveEvents.length > 0 && (
        <section className="relative z-10 py-12 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-75" />
                <div className="relative w-3 h-3 bg-red-500 rounded-full" />
              </div>
              <h2 className="text-2xl font-bold text-white">Live Now</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {liveEvents.map((event) => (
                <div 
                  key={event.id}
                  onClick={() => handleEventClick(event)}
                  className="group relative bg-gradient-to-br from-red-900/40 to-purple-900/40 rounded-2xl overflow-hidden cursor-pointer border border-red-500/30 hover:border-red-500/60 transition-all duration-300"
                >
                  {/* Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <div className="relative p-6">
                    <div className="flex items-start justify-between mb-4">
                      <Badge className="bg-red-500 text-white animate-pulse">
                        🔴 LIVE NOW
                      </Badge>
                      <div className="flex gap-2">
                        {hasTicketForEvent(event.id) && (
                          <Badge className="bg-green-600/80 text-white">
                            ✓ Ticket
                          </Badge>
                        )}
                        {event.voting_enabled && (
                          <Badge className="bg-purple-600/80 text-white">
                            🗳️ Voting
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-red-300 transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">{event.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-white">
                        {event.display_symbol || (event.currency === 'GBP' ? '£' : '$')}{(event.display_price ?? event.price).toFixed(2)}
                      </span>
                      <Button className={`${hasTicketForEvent(event.id) ? 'bg-green-500 hover:bg-green-400' : 'bg-white hover:bg-gray-200'} text-black group-hover:scale-105 transition-transform`}>
                        {hasTicketForEvent(event.id) ? 'Watch Now' : 'Get Ticket'}
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Banner Advertisement */}
      <BannerAd platform="live" />

      {/* Upcoming Events */}
      <section id="events" className="relative z-10 py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-white">Upcoming Events</h2>
            <div className="text-sm text-gray-500">{upcomingEvents.length} {upcomingEvents.length === 1 ? 'event' : 'events'}</div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Loading events...</p>
            </div>
          ) : upcomingEvents.length === 0 ? (
            <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10">
              <Zap className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No Upcoming Events</h3>
              <p className="text-gray-400">Check back soon for new live streams!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingEvents.map((event) => (
                <div 
                  key={event.id}
                  onClick={() => handleEventClick(event)}
                  className="group relative bg-white/5 hover:bg-white/10 rounded-2xl overflow-hidden cursor-pointer border border-white/10 hover:border-white/20 transition-all duration-300"
                >
                  {/* Thumbnail */}
                  <div className="relative h-48 bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden">
                    {event.thumbnail_url ? (
                      <>
                        {/* Mobile: Use mobile thumbnail if available */}
                        <img 
                          src={event.mobile_thumbnail_url || event.thumbnail_url} 
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 sm:hidden"
                        />
                        {/* Desktop: Always use desktop thumbnail */}
                        <img 
                          src={event.thumbnail_url} 
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 hidden sm:block"
                        />
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-900/30 to-purple-900/30">
                        <Play className="h-12 w-12 text-white/30" />
                      </div>
                    )}
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    
                    {/* Date Badge */}
                    <div className="absolute top-4 left-4 bg-[#0a0a0a]/60 backdrop-blur-sm rounded-lg px-3 py-2 text-center">
                      <div className="text-lg font-bold text-white">{formatDate(event.event_date).split(' ')[0]}</div>
                      <div className="text-xs text-gray-400">{formatDate(event.event_date).split(' ')[1]}</div>
                    </div>
                    
                    <div className="absolute top-4 right-4 flex gap-2">
                      {hasTicketForEvent(event.id) && (
                        <Badge className="bg-green-600/80 backdrop-blur-sm">✓ Ticket</Badge>
                      )}
                      {event.voting_enabled && (
                        <Badge className="bg-purple-600/80 backdrop-blur-sm">🗳️ Voting</Badge>
                      )}
                    </div>
                    
                    {/* Price */}
                    <div className="absolute bottom-4 right-4 bg-white text-black font-bold px-3 py-1 rounded-lg">
                      {event.display_symbol || (event.currency === 'GBP' ? '£' : '$')}{(event.display_price ?? event.price).toFixed(2)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-red-400 transition-colors line-clamp-1">
                      {event.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">{event.description}</p>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-gray-500">
                        <Clock className="h-4 w-4" />
                        <span>{formatTime(event.event_date)}</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className={`${hasTicketForEvent(event.id) ? 'text-green-400 hover:text-green-300 hover:bg-green-500/10' : 'text-red-400 hover:text-red-300 hover:bg-red-500/10'} p-0`}
                      >
                        {hasTicketForEvent(event.id) ? 'Watch Stream' : 'Get Ticket'}
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <section className="relative z-10 py-12 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-500">Past Events</h2>
              <div className="text-sm text-gray-600">{pastEvents.length} {pastEvents.length === 1 ? 'event' : 'events'}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pastEvents.map((event) => (
                <div 
                  key={event.id}
                  onClick={() => handleEventClick(event)}
                  className="group relative bg-white/[0.03] rounded-2xl overflow-hidden cursor-pointer border border-white/[0.06] hover:border-white/10 transition-all duration-300 opacity-60 hover:opacity-80"
                >
                  {/* Thumbnail */}
                  <div className="relative h-48 bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden">
                    {event.thumbnail_url ? (
                      <>
                        <img 
                          src={event.mobile_thumbnail_url || event.thumbnail_url} 
                          alt={event.title}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-[50%] transition-all duration-500 sm:hidden"
                        />
                        <img 
                          src={event.thumbnail_url} 
                          alt={event.title}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-[50%] transition-all duration-500 hidden sm:block"
                        />
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800/30 to-gray-900/30">
                        <Play className="h-12 w-12 text-white/20" />
                      </div>
                    )}
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    
                    {/* Past Event Badge */}
                    <div className="absolute top-4 left-4 bg-gray-800/80 backdrop-blur-sm rounded-lg px-3 py-1">
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Past Event</span>
                    </div>
                    
                    {/* Date Badge */}
                    <div className="absolute bottom-4 left-4 bg-[#0a0a0a]/60 backdrop-blur-sm rounded-lg px-3 py-2 text-center">
                      <div className="text-sm font-bold text-gray-400">{formatDate(event.event_date)}</div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-400 mb-2 group-hover:text-gray-300 transition-colors line-clamp-1">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="h-4 w-4" />
                        <span>{formatTime(event.event_date)}</span>
                      </div>
                      {hasTicketForEvent(event.id) && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-gray-500 hover:text-gray-300 hover:bg-white/5 p-0"
                        >
                          View Recording
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="relative z-10 py-16 px-4 sm:px-6 bg-gradient-to-b from-transparent to-white/5">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose LTV Live?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                <Play className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">4K / HD Streaming</h3>
              <p className="text-gray-400 text-sm">Crystal clear video quality powered by Cloudflare's global network</p>
            </div>
            
            <div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Live Chat</h3>
              <p className="text-gray-400 text-sm">Interact with other viewers in real-time during the stream</p>
            </div>
            
            <div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Star className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Live Voting</h3>
              <p className="text-gray-400 text-sm">Vote for your favorites in battles and competitions</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-8 px-4 sm:px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">© 2026 LTV Live. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="/" className="text-gray-400 hover:text-white transition-colors text-sm">Home</a>
            <a href="/about" className="text-gray-400 hover:text-white transition-colors text-sm">About</a>
            {isLoggedIn ? (
              <button 
                onClick={() => {
                  localStorage.removeItem('livestreamToken');
                  localStorage.removeItem('livestreamCustomer');
                  setIsLoggedIn(false);
                  setIsAdmin(false);
                }}
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Sign Out
              </button>
            ) : (
              <a href="/live/login" className="text-gray-400 hover:text-white transition-colors text-sm">Sign In</a>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LiveEventsLanding;
